// BI external connections (per-project Metadata_Model/connections) —
// analytics data sources a schema / business area / visual / report can
// target instead of the project's own database. Supports classic RDBMS
// (PostgreSQL/MySQL/Oracle/SQL Server/SQLite) and analytical warehouses
// (Snowflake / Google BigQuery / Dremio via Arrow Flight / SAP HANA).
// Distinct from /project-db/connections (the app's runtime DB).
import api from './client'

export interface BiConnectionLastTest {
  status: 'ok' | 'failed'
  message?: string
  tested_at?: string
}

export interface BiConnection {
  CONNECTION_ID: string
  CONNECTION_NAME?: string
  DATABASE_TYPE?: string
  HOST?: string
  PORT?: number | null
  USERNAME?: string
  DATABASE?: string
  SCHEMA?: string
  ORACLE_CONNECT_TYPE?: string
  // Per-type extras (no secrets): Snowflake ACCOUNT/WAREHOUSE/ROLE, BigQuery
  // PROJECT_ID/DATASET/MAX_BYTES_BILLED, Dremio TLS/FLIGHT_PORT, HANA ENCRYPT.
  OPTIONS?: Record<string, unknown>
  // Per-connection statement-timeout override. Unset = the instance default;
  // it may never exceed the instance maximum (served, resolved, as
  // ConnectionTypeCatalog.max_query_timeout_seconds).
  QUERY_TIMEOUT_SECONDS?: number | null
  // Stamped by the backend on every Test connection run — powers the health
  // dots (green = last test ok, red = failed, grey = never tested).
  LAST_TEST?: BiConnectionLastTest
}

/** Health-dot color role for a connection (spec §0.5). */
export function connectionHealthColor(c: BiConnection): string {
  const s = c.LAST_TEST?.status
  if (s === 'ok') return 'var(--color-success)'
  if (s === 'failed') return 'var(--color-error)'
  return 'var(--color-text-secondary)'
}

export interface BiConnectionDraft extends BiConnection {
  project: string
  // Secrets — write-only: encrypted at rest, never returned by the API.
  PASSWORD?: string
  PRIVATE_KEY?: string
  SERVICE_ACCOUNT_JSON?: string
}

/** One database type the connection picker can offer.
 *
 * Served by GET /connections/types, which derives every field from the ONE
 * registry in twasta_runtime_engine/bi_dialect.py — so a database added there
 * appears in the picker with no change to this file. Never hard-code a type
 * list here again. */
export interface ConnectionType {
  value: string
  label: string
  /** Two-letter tile badge. */
  badge: string
  /** Picker section key — see ConnectionTypeCategory. */
  category: string
  description: string
  /** The SQL dialect it speaks ('' for the gateway transport). */
  dialect: string
  default_port: number | null
  /** Which credential form to render: classic | sqlite | snowflake | bigquery
   *  | databricks | clickhouse | trino | athena | duckdb | dremio | hana |
   *  synapse | gateway. */
  form: string
  /** Analytical read-only source — never a write target. */
  warehouse: boolean
  /** False only for 'gateway' (a gateway cannot front another gateway). */
  gateway_frontable: boolean
  /** How many connections the caller's accessible projects already have of
   *  this type. Drives the picker's pinned "In use" section. */
  in_use: number
  /** False when the Python driver is absent — the card says so up front
   *  rather than letting Test fail with an opaque import error. */
  driver_installed: boolean
  install_hint: string | null
}

export interface ConnectionTypeCategory {
  key: string
  label: string
}

export interface ConnectionTypeCatalog {
  types: ConnectionType[]
  categories: ConnectionTypeCategory[]
  /** The instance-admin ceiling on a connection's own QUERY_TIMEOUT_SECONDS,
   *  already resolved (never null). Served here because ordinary project users
   *  cannot call the instance-admin endpoints that own the setting. */
  max_query_timeout_seconds: number
}

export async function listConnectionTypes(): Promise<ConnectionTypeCatalog> {
  const res = await api.get('/connections/types')
  return {
    types: (res.data?.types ?? []) as ConnectionType[],
    categories: (res.data?.categories ?? []) as ConnectionTypeCategory[],
    max_query_timeout_seconds: Number(res.data?.max_query_timeout_seconds) || 600,
  }
}

export async function listBiConnections(project: string): Promise<BiConnection[]> {
  const res = await api.get('/connections/list', { params: { project } })
  return (res.data?.connections ?? []) as BiConnection[]
}

export async function saveBiConnection(draft: BiConnectionDraft): Promise<BiConnection> {
  const res = await api.post('/connections/save', draft)
  return res.data?.connection as BiConnection
}

export async function deleteBiConnection(project: string, connectionId: string): Promise<boolean> {
  const res = await api.post('/connections/delete', null, {
    params: { project, connection_id: connectionId },
  })
  return !!res.data?.deleted
}

export interface BiConnectionTestResult {
  success: boolean
  message?: string
  error?: string
}

export async function testBiConnection(
  project: string, connectionId: string,
): Promise<BiConnectionTestResult> {
  const res = await api.post('/connections/test', null, {
    params: { project, connection_id: connectionId },
  })
  return res.data as BiConnectionTestResult
}

export interface BiIntrospectResult {
  success: boolean
  connection?: string
  db_type?: string
  schema?: string
  tables?: string[] | Array<Record<string, unknown>>
  views?: string[]
  notes?: string[]
}

export async function introspectBiConnection(
  project: string, connectionId: string, opts?: { schema?: string; tables?: string },
): Promise<BiIntrospectResult> {
  const res = await api.get('/connections/introspect', {
    params: {
      project, connection_id: connectionId,
      ...(opts?.schema ? { schema: opts.schema } : {}),
      ...(opts?.tables ? { tables: opts.tables } : {}),
    },
  })
  return res.data as BiIntrospectResult
}
